import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Table from "@mui/joy/Table";
import Link from "@mui/joy/Link";
import { AnalyticsFilters, AnalyticsFiltersState } from "../components/AnalyticsFilters";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    ScrollTable,
    WatchSignals,
} from "../components/AnalyticsShared";
import { useAsyncBlock } from "../hooks";
import { bidsTopSkinsApi } from "../../bidsApi";
import { BidTopSkinInfo } from "../../../../models/bidAnalytics";
import { formatInt, formatMoneyKopecks, gameName } from "../format";
import { Game } from "../../../../models/games";

type Props = {
    isActive: boolean;
    filters: AnalyticsFiltersState;
    onFiltersChange: (next: AnalyticsFiltersState) => void;
    games: Game[];
    gamesLoading: boolean;
};

export function TopSkinsTab(props: Props) {
    const { isActive, filters, onFiltersChange, games, gamesLoading } = props;

    const [state, refresh] = useAsyncBlock<BidTopSkinInfo[]>(
        () =>
            bidsTopSkinsApi({
                from: filters.from,
                to: filters.to,
                gameId: filters.gameId,
                take: filters.take,
            }),
        isActive,
        [filters.from, filters.to, filters.gameId, filters.take]
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <AnalyticsFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                games={games}
                gamesLoading={gamesLoading}
                showPeriod
                showTake
            />

            <AnalyticsCard
                title="Топ скинов по спросу"
                subtitle="Горячие позиции для ликвидности, контента и контроля цен."
                snapshotLabel="period"
                howToRead={
                    <Typography level="body-sm">
                        Сортировка по сумме остатка. Сравнивайте «остаток» и «выкупили». Много
                        уникальных покупателей = широкий интерес.
                    </Typography>
                }
                watchFor={
                    <WatchSignals
                        items={[
                            {
                                signal: "Выкуп относительно остатка",
                                ifGrows: "остаток ≈ выкупили — спрос закрывается нормально.",
                                ifFalls: "скин «горит», но не закрывается — мало офферов или цена заявки ниже рынка; смотрите спред.",
                            },
                            {
                                signal: "Число уникальных покупателей на скин",
                                ifGrows: "интерес широкий, позиция устойчивее.",
                                ifFalls: "концентрация на 1–2 байерах — риск, что уйдёт один крупный клиент и топ «схлопнется».",
                            },
                            {
                                signal: "Сумма спроса по скину",
                                ifGrows: "рост вместе с выкупом — ликвидная горячая позиция; без выкупа — копится неудовлетворённый спрос.",
                                ifFalls: "интерес к позиции слабеет.",
                            },
                        ]}
                    />
                }
            >
                {state.loading && <BlockLoading />}
                {!state.loading && state.error && <BlockError message={state.error} onRetry={refresh} />}
                {!state.loading && !state.error && (!state.data || state.data.length === 0) && (
                    <EmptyState />
                )}
                {!state.loading && !state.error && state.data && state.data.length > 0 && (
                    <ScrollTable>
                        <Table size="sm" stickyHeader sx={{ minWidth: 900 }}>
                            <thead>
                                <tr>
                                    <th>Скин</th>
                                    <th>Игра</th>
                                    <th>Заявок</th>
                                    <th>Покупателей</th>
                                    <th>Остаток</th>
                                    <th>Выкупили</th>
                                    <th>Макс. цена</th>
                                    <th>Сумма спроса</th>
                                </tr>
                            </thead>
                            <tbody>
                                {state.data.map((row) => (
                                    <tr key={`${row.marketSkinId}-${row.hashName}`}>
                                        <td>
                                            <Link
                                                level="body-sm"
                                                href={`/app/bids?hashName=${encodeURIComponent(row.hashName)}#list`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {row.hashName || row.marketSkinId}
                                            </Link>
                                        </td>
                                        <td>{gameName(row.gameId)}</td>
                                        <td>{formatInt(row.bidsCount)}</td>
                                        <td>{formatInt(row.uniqueBuyersCount)}</td>
                                        <td>{formatInt(row.totalCount)}</td>
                                        <td>{formatInt(row.totalBought)}</td>
                                        <td>{formatMoneyKopecks(row.maxPrice)}</td>
                                        <td>{formatMoneyKopecks(row.totalSum)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </ScrollTable>
                )}
            </AnalyticsCard>
        </Box>
    );
}
